import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useDebounce } from '@aimsos/hooks';
import { listMarketplace } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import { mapMarketplaceCardToUi } from '../../utils';

/**
 * Sort field → its natural default direction, applied whenever the user picks
 * a new field via `Filters`' sort dropdown (the direction toggle then flips it
 * independently — e.g. "Newest first" reversed means "Oldest first").
 * TODO(CT-3904 / backend): `newest`/`popular` need real fields; both map to
 * `order_by: 'name'` for now, mocked here.
 */
const SORT_FIELD_DEFAULT_DIRECTION = {
  az: 'asc',
  newest: 'desc',
  popular: 'asc',
};

const PER_PAGE = 12;

/**
 * Browse-All state (CT-4166): search (debounced) + sort (field + direction,
 * driven by `@aimsos/ui`'s `Filters` `sort` prop) + applied filters +
 * pagination over `marketplaceService.listMarketplace`. Resets to page 1 on any
 * search/sort/filter change. `setFilters` is the Apply target from the Filters
 * Slideout (CT-4168); `clearAll` is the Empty-state escape route (CT-4169).
 */
export function useMarketplace() {
  const { t } = useTranslation('admin-studio');

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  // Clearing to empty should feel instant — no reason to wait out the debounce
  // for a query the user isn't actively typing anymore (Clear Filters relies
  // on this to restore the grid immediately instead of racing the old query).
  const effectiveSearch = search === '' ? '' : debouncedSearch;
  const [sortBy, setSortByState] = useState('az');
  const [sortDirection, setSortDirection] = useState(SORT_FIELD_DEFAULT_DIRECTION.az);
  const [filters, setFiltersState] = useState({});
  const [page, setPage] = useState(1);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Picking a new sort field resets direction to that field's natural default
  // (e.g. switching to "Newest first" starts at desc, not whatever asc/desc the
  // previous field left behind).
  const setSortBy = useCallback((field) => {
    setSortByState(field);
    setSortDirection(SORT_FIELD_DEFAULT_DIRECTION[field] ?? 'asc');
  }, []);

  // Reset to page 1 whenever the query/sort/filters change.
  useEffect(() => {
    setPage(1);
  }, [effectiveSearch, sortBy, sortDirection, filters]);

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const res = await listMarketplace({
        page,
        page_size: PER_PAGE,
        order_by: 'name',
        order_direction: sortDirection,
        filters,
        search: { query: effectiveSearch, fields: ['name', 'category'] },
      });
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      const normalized = validateApiResponse(res, t('common.requestFailed'));
      const rows = Array.isArray(normalized.body) ? normalized.body : [];
      setItems(rows.map(mapMarketplaceCardToUi));
      setTotal(Number(normalized.meta?.total ?? rows.length));
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setError(err?.message ?? t('common.requestFailed'));
      setItems([]);
      setTotal(0);
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) setIsLoading(false);
    }
  }, [page, sortDirection, filters, effectiveSearch, t]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilters = useCallback((applied) => setFiltersState(applied ?? {}), []);
  const clearAll = useCallback(() => {
    setSearch('');
    setFiltersState({});
  }, []);

  const activeFilterCount = useMemo(
    () =>
      Object.values(filters).reduce(
        (sum, v) => sum + (Array.isArray(v) ? v.length : v ? 1 : 0),
        0,
      ),
    [filters],
  );

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return useMemo(
    () => ({
      items,
      total,
      isLoading,
      error,
      search,
      setSearch,
      sortBy,
      setSortBy,
      sortDirection,
      setSortDirection,
      filters,
      setFilters,
      clearAll,
      activeFilterCount,
      page,
      setPage,
      perPage: PER_PAGE,
      totalPages,
    }),
    [
      items,
      total,
      isLoading,
      error,
      search,
      sortBy,
      setSortBy,
      sortDirection,
      filters,
      setFilters,
      clearAll,
      activeFilterCount,
      page,
      totalPages,
    ],
  );
}
